import React, { useCallback, useRef } from 'react';
import {
  Image as ImageIcon, Film, Plus, X, Minus, Loader2, Sparkles,
} from 'lucide-react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';
import { IMAGE_MODELS } from '../../constants/image-generation';
import type { ScreenComponentDef } from '@core/types/slot-screen-component.types';
import { useScreenComponentGenerator } from '@hooks/useScreenComponentGenerator';

const IMAGE_COUNT_OPTIONS: Array<1 | 2 | 4 | 8> = [1, 2, 4, 8];
const VIDEO_COUNT_OPTIONS: Array<1 | 2 | 4> = [1, 2, 4];

const VIDEO_MODELS = [
  { id: 'veo-3.1', name: 'Veo 3.1' },
];

interface GeneratorPromptBarProps {
  component: ScreenComponentDef;
}

export function GeneratorPromptBar({ component }: GeneratorPromptBarProps) {
  const activeScreenId = useSlotScreenGeneratorStore(s => s.activeScreenId);
  const globalModel = useSlotScreenGeneratorStore(s => s.generationSettings.model);
  const globalImageCount = useSlotScreenGeneratorStore(s => s.generationSettings.imageCount);
  const updateComponentPrompt = useSlotScreenGeneratorStore(s => s.updateComponentPrompt);
  const setComponentGenerationMode = useSlotScreenGeneratorStore(s => s.setComponentGenerationMode);
  const updateComponentGenerationSettings = useSlotScreenGeneratorStore(s => s.updateComponentGenerationSettings);
  const addComponentReferenceImage = useSlotScreenGeneratorStore(s => s.addComponentReferenceImage);
  const removeComponentReferenceImage = useSlotScreenGeneratorStore(s => s.removeComponentReferenceImage);

  const { generate, cancel, isGenerating } = useScreenComponentGenerator();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const mode = component.generationMode;
  const model = component.generationModel ?? globalModel;
  const imageCount = component.generationImageCount ?? globalImageCount;

  const countOptions = mode === 'video' ? VIDEO_COUNT_OPTIONS : IMAGE_COUNT_OPTIONS;
  const maxCount = mode === 'video' ? 4 : 8;

  const allModels = mode === 'video'
    ? [...IMAGE_MODELS, ...VIDEO_MODELS]
    : IMAGE_MODELS;

  const selectedModelLabel = allModels.find(m => m.id === model)?.name ?? model;

  const hasPrompt = !!(component.userPrompt.trim() || component.defaultPromptHint);

  const handleGenerate = useCallback(() => {
    generate();
  }, [generate]);

  const handleCancel = useCallback(() => {
    cancel();
  }, [cancel]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (hasPrompt && !isGenerating) {
        handleGenerate();
      }
    }
  }, [hasPrompt, isGenerating, handleGenerate]);

  const handleModeChange = useCallback((newMode: 'image' | 'video') => {
    setComponentGenerationMode(activeScreenId, component.id, newMode);
  }, [activeScreenId, component.id, setComponentGenerationMode]);

  const handleModelChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    updateComponentGenerationSettings(activeScreenId, component.id, { generationModel: e.target.value });
  }, [activeScreenId, component.id, updateComponentGenerationSettings]);

  const handleCountChange = useCallback((dir: 'prev' | 'next') => {
    const idx = countOptions.indexOf(imageCount as typeof countOptions[number]);
    if (dir === 'prev' && idx > 0) {
      updateComponentGenerationSettings(activeScreenId, component.id, { generationImageCount: countOptions[idx - 1] as 1 | 2 | 4 | 8 });
    } else if (dir === 'next' && idx < countOptions.length - 1) {
      updateComponentGenerationSettings(activeScreenId, component.id, { generationImageCount: countOptions[idx + 1] as 1 | 2 | 4 | 8 });
    }
  }, [activeScreenId, component.id, imageCount, countOptions, updateComponentGenerationSettings]);

  // Reference image handling
  const addImageFiles = useCallback((files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      if (file.type.startsWith('image/')) {
        addComponentReferenceImage(activeScreenId, component.id, URL.createObjectURL(file));
      }
    }
  }, [activeScreenId, component.id, addComponentReferenceImage]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      addImageFiles(e.target.files);
      e.target.value = '';
    }
  }, [addImageFiles]);

  const handleRemoveRefImage = useCallback((index: number) => {
    const url = component.referenceImages[index];
    if (url?.startsWith('blob:')) {
      try { URL.revokeObjectURL(url); } catch { /* ignore */ }
    }
    removeComponentReferenceImage(activeScreenId, component.id, index);
  }, [activeScreenId, component.id, component.referenceImages, removeComponentReferenceImage]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      addImageFiles(imageFiles);
    }
  }, [addImageFiles]);

  return (
    <div className="flex-shrink-0 border-t border-zinc-800 bg-zinc-900/80 px-4 py-3">
      {/* Reference images row */}
      {component.referenceImages.length > 0 && (
        <div className="flex gap-2 mb-2 flex-wrap">
          {component.referenceImages.map((url, idx) => (
            <div key={idx} className="relative group/ref w-10 h-10 rounded overflow-hidden border border-zinc-700 flex-shrink-0">
              <img src={url} alt={`ref ${idx + 1}`} className="w-full h-full object-cover" />
              <button
                onClick={() => handleRemoveRefImage(idx)}
                className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover/ref:opacity-100 transition-opacity"
              >
                <X size={10} className="text-zinc-200" />
              </button>
            </div>
          ))}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-10 h-10 rounded border border-dashed border-zinc-700 flex items-center justify-center text-zinc-500 hover:text-zinc-300 hover:border-zinc-500 transition-colors"
          >
            <Plus size={14} />
          </button>
        </div>
      )}

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={component.userPrompt}
        onChange={e => updateComponentPrompt(activeScreenId, component.id, e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={component.defaultPromptHint || 'Describe what to generate...'}
        rows={2}
        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 resize-none outline-none focus:ring-1 focus:ring-yellow-500/50 placeholder:text-zinc-600 leading-relaxed"
      />

      {/* Controls row */}
      <div className="flex items-center gap-2 mt-2">
        {/* Image/Video toggle */}
        <div className="flex bg-zinc-800 rounded-lg p-0.5">
          <button
            className={`px-2.5 py-1 text-xs rounded-md flex items-center gap-1 transition-colors ${
              mode === 'image' ? 'bg-zinc-600 text-white' : 'text-zinc-400 hover:text-zinc-300'
            }`}
            onClick={() => handleModeChange('image')}
          >
            <ImageIcon size={12} />
            Image
          </button>
          <button
            className={`px-2.5 py-1 text-xs rounded-md flex items-center gap-1 transition-colors ${
              mode === 'video' ? 'bg-zinc-600 text-white' : 'text-zinc-400 hover:text-zinc-300'
            }`}
            onClick={() => handleModeChange('video')}
          >
            <Film size={12} />
            Video
          </button>
        </div>

        {/* Model selector */}
        <select
          value={model}
          onChange={handleModelChange}
          className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-300 outline-none hover:border-zinc-500 transition-colors max-w-[140px] truncate"
        >
          {allModels.map(m => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>

        {/* Count stepper */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={() => handleCountChange('prev')}
            disabled={imageCount <= 1}
            className="p-1 text-zinc-400 hover:text-zinc-200 disabled:text-zinc-600 disabled:cursor-not-allowed rounded hover:bg-zinc-700 transition-colors"
          >
            <Minus size={12} />
          </button>
          <span className="text-xs text-zinc-300 tabular-nums w-8 text-center">{imageCount}/{maxCount}</span>
          <button
            onClick={() => handleCountChange('next')}
            disabled={imageCount >= maxCount}
            className="p-1 text-zinc-400 hover:text-zinc-200 disabled:text-zinc-600 disabled:cursor-not-allowed rounded hover:bg-zinc-700 transition-colors"
          >
            <Plus size={12} />
          </button>
        </div>

        {/* Aspect ratio display */}
        <span className="text-xs text-zinc-500 flex-shrink-0">
          {component.width}:{component.height}
        </span>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Add reference (when no refs yet) */}
        {component.referenceImages.length === 0 && (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            <Plus size={12} />
            Ref
          </button>
        )}

        {/* Generate / Cancel button */}
        {isGenerating ? (
          <button
            onClick={handleCancel}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 border border-zinc-600 rounded-lg hover:bg-zinc-700 transition-colors"
          >
            <X size={12} />
            Cancel
          </button>
        ) : (
          <button
            onClick={handleGenerate}
            disabled={!hasPrompt}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed transition-colors"
          >
            <Sparkles size={12} />
            Generate
          </button>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileInput}
      />
    </div>
  );
}
